let unityInstance = null;
let gameObjectName = 'MessageReceiver';
let seq = 0;
const queue = [];
const LEGACY_Q_NAME = '__unityQueue';
let hasAdopted = false;
let viewToggleCallback = null;


/**
 * Send a start session event to unity
 * @param {object} messageBody The message to send.
 */
function startSession(messageBody) {
  if (unityInstance) {
    try {
      unityInstance.SendMessage(
        gameObjectName,
        'StartSession',
        JSON.stringify(messageBody)
      );
    } catch (err) {
      console.error('Failed to send start session to Unity:', err);
    }
  } else {
    queue.push(messageBody);
  }
}
/**
 * End a start session event to unity
 */
function endSession() {
  if (unityInstance) {
    try {
      unityInstance.SendMessage(
        gameObjectName,
        'EndSession'
      );
    } catch (err) {
      console.error('Failed to send end session to Unity:', err);
    }
  } else {
    queue.push(messageBody);
  }
}

/**
 * The core function that either sends a message to the active Unity instance or queues it.
 * @param {object} messageBody The message to send.
 */
function postToUnity(messageBody) {
  if (unityInstance) {
    try {
      unityInstance.SendMessage(
        gameObjectName,
        'ReceiveMessage',
        JSON.stringify(messageBody)
      );
    } catch (err) {
      console.error('Failed to send message to Unity:', err);
    }
  } else {
    queue.push(messageBody);
  }
}

/**
 * Adopts the legacy queue used by the Scratch extension.
 * It drains any existing messages and then replaces the global property with a
 * shim that intercepts all future assignments and pushes, redirecting them to our queue.
 */
function adoptLegacyQueue() {
  if (typeof window === 'undefined' || hasAdopted) return;
  hasAdopted = true;

  const legacy = window[LEGACY_Q_NAME];
  if (Array.isArray(legacy)) {
    for (const msg of legacy) queue.push(msg);
  }

  try {
    let backing = []; 
    Object.defineProperty(window, LEGACY_Q_NAME, {
      configurable: true,
      enumerable: false,
      get: () => {
        return {
          push: (msg) => { queue.push(msg); return queue.length; },
          get length() { return queue.length; }
        };
      },
      set: (v) => {
        if (Array.isArray(v)) {
          for (const msg of v) queue.push(msg);
        }
      }
    });
  } catch {
    window[LEGACY_Q_NAME] = {
      push: (msg) => queue.push(msg)
    };
  }
}

/**
 * Registers the active Unity instance, making it available for message sending.
 * @param {object} instance The Unity instance from `createUnityInstance`.
 * @param {string} goName The name of the target GameObject.
 */
export function registerUnityInstance(instance, goName) {
  unityInstance = instance;
  if (goName) gameObjectName = goName;

  window.sendToUnity = postToUnity;
  window.startSession = startSession;
  window.endSession = endSession;

  while (queue.length > 0) {
    postToUnity(queue.shift());
  }
}

/**
 * Unregisters a Unity instance. This is HMR-safe.
 * It will only clear the active instance if it's the one being unregistered,
 * preventing a late-firing cleanup from a previous component instance from
 * deactivating a newer, active one.
 * @param {object} instance The specific instance to unregister.
 */
export function unregisterUnityInstance(instance) {
  if (unityInstance === instance) {
    unityInstance = null;
    if (window.sendToUnity) {
      delete window.sendToUnity;
    }
  }
}

/**
 * The primary, modern API for sending messages to Unity from your application.
 * @param {string} op The operation code.
 * @param {object} data The message payload.
 */
export function sendUnity(op, data = {}) {
  const messageBody = { seq: ++seq, op, data };
  postToUnity(messageBody);
}

adoptLegacyQueue();

/**
 * Allows a React component to register its state-updater function.
 * @param {function} callback The function to call to toggle the view.
 */
export function registerToggleViewCallback(callback) {
    viewToggleCallback = callback;
}

/**
 * Clears the callback when the component unmounts.
 */
export function unregisterToggleViewCallback() {
    viewToggleCallback = null;
}

/**
 * Expose a function on the window object that Unity can call.
 * This function will trigger the registered callback if it exists.
 * @param {boolean} showYouTube - true to show YouTube, false to show Unity.
 */
window.setUnityYouTubeViewVisibility = function(showYouTube) {
    if (typeof viewToggleCallback === 'function') {
        console.log(`Setting view visibility: showYouTube = ${showYouTube}`);
        viewToggleCallback(showYouTube);
    } else {
        console.warn('setUnityYouTubeViewVisibility was called, but no callback is registered.');
    }
};

export function sendClientHandshake(handshake) {
  if (!window.__unityInstance) return;
  window.__unityInstance.SendMessage("SessionManager", "ReceiveClientHandshake", JSON.stringify(handshake));
}